/**
 * Scroll-depth + section-visibility tracking for marketing pages.
 *
 * Fires:
 *   • `scroll_depth`  once each when the user reaches 25 / 50 / 75 / 100 %
 *     of the page height. Includes `page`, `depth`, `ms_since_load`.
 *   • `section_viewed` once per section when ≥ 50 % of it has been visible
 *     for at least 400 ms. Includes `page`, `section`, `ms_since_load`.
 *
 * Both are persisted through `trackFunnelEvent` (cast to bypass the strict
 * union) so the admin dashboard can aggregate them the same way as CTA
 * impressions/clicks.
 */
import { useEffect } from "react";
import { trackEvent } from "@/lib/observability";
import { trackFunnelEvent } from "@/lib/funnelAnalytics";

type AnyFunnelTracker = (name: string, meta: Record<string, unknown>) => void;
const persist = trackFunnelEvent as unknown as AnyFunnelTracker;

const DEPTH_THRESHOLDS = [25, 50, 75, 100] as const;

/** Install a scroll-depth listener for the current page. */
export function useScrollDepth(page: string) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const start = performance.now();
    const fired = new Set<number>();

    const compute = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = Math.max(doc.scrollHeight, window.innerHeight);
      const pct = Math.min(100, Math.round((scrolled / total) * 100));
      for (const t of DEPTH_THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          const payload = {
            page,
            depth: t,
            ms_since_load: Math.round(performance.now() - start),
          };
          trackEvent("scroll_depth", payload);
          persist("scroll_depth", payload);
        }
      }
      if (fired.size === DEPTH_THRESHOLDS.length) detach();
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        compute();
      });
    };

    const detach = () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    // Fire an initial sample in case the page is short enough to already be > 25 %.
    compute();
    return detach;
  }, [page]);
}

/** Track a single section-view event (fire-and-forget, idempotent per session). */
const sessionSeen = new Set<string>();
export function trackSectionView(
  page: string,
  section: string,
  msSinceLoad?: number,
) {
  const key = `${page}::${section}`;
  if (sessionSeen.has(key)) return;
  sessionSeen.add(key);
  const payload = {
    page,
    section,
    ms_since_load: msSinceLoad ?? Math.round(performance.now()),
  };
  trackEvent("section_viewed", payload);
  persist("section_viewed", payload);
}
